import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { deleteEvidence, getMyEvidence, saveEvidence } from "./appraisalApi";
import {
  AppraisalGuideButton,
  AppraisalHelpModal,
  AppraisalSectionHeader,
} from "./AppraisalHelp";
import { APPRAISAL_LIMITS, clampLen, validateFields } from "./appraisalValidation";
import { AppraisalCharCount } from "./AppraisalFormFields";
import "./Appraisal.css";

const emptyForm = () => ({
  headline: "",
  personalContribution: "",
  fileName: "",
  fileUrl: "",
  isCompleted: false,
});

const normalizeEvidence = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  return {
    id: raw.id ?? raw.Id,
    headline: raw.headline ?? raw.Headline ?? "",
    personalContribution: raw.personalContribution ?? raw.PersonalContribution ?? "",
    fileName: raw.fileName ?? raw.FileName ?? "",
    fileUrl: raw.fileUrl ?? raw.FileUrl ?? "",
    isCompleted: Boolean(raw.isCompleted ?? raw.IsCompleted),
    createdDate: raw.createdDate ?? raw.CreatedDate,
  };
};

const AppraisalEvidencePage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [helpKey, setHelpKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const data = await getMyEvidence();
      setRows(
        (Array.isArray(data) ? data : []).map(normalizeEvidence).filter(Boolean)
      );
    } catch (e) {
      setError(e?.message || "Failed to load evidence");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async (e) => {
    e.preventDefault();
    if (saving) return;
    setMessage("");
    setError("");
    const validationError = validateFields([
      { value: form.headline, required: true, max: APPRAISAL_LIMITS.headline, label: "Headline" },
      {
        value: form.personalContribution,
        max: APPRAISAL_LIMITS.longText,
        label: "Personal contribution",
      },
      { value: form.fileName, max: APPRAISAL_LIMITS.fileName, label: "File name" },
    ]);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    try {
      await saveEvidence(form);
      setForm(emptyForm());
      setMessage(
        t("app.appraisal.evidence.saved", { defaultValue: "Evidence saved." })
      );
      await load({ silent: true });
    } catch (err) {
      setError(err?.message || "Failed to save evidence");
    } finally {
      setSaving(false);
    }
  };

  const openDeleteConfirm = (item) => {
    setDeleteConfirm({
      id: item.id,
      name: item.headline,
      title: t("app.appraisal.evidence.deleteTitle", {
        defaultValue: "Remove evidence",
      }),
      body: t("app.appraisal.evidence.deleteBody", {
        defaultValue: "Remove “{{name}}”? This cannot be undone.",
        name: item.headline,
      }),
    });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleteLoading(true);
    setError("");
    try {
      await deleteEvidence(deleteConfirm.id);
      setDeleteConfirm(null);
      setMessage(
        t("app.appraisal.evidence.removed", {
          defaultValue: "Evidence removed.",
        })
      );
      await load({ silent: true });
    } catch (err) {
      setError(err?.message || "Failed to delete evidence");
      setDeleteConfirm(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const completedCount = rows.filter((r) => r.isCompleted).length;

  return (
    <div className="appraisal-page appraisal-evidence-page">
      <CommonPageHeader
        icon={<i className="bi bi-folder-symlink" />}
        title={t("app.appraisal.evidenceTitle", { defaultValue: "Evidence" })}
        description={t("app.appraisal.evidenceSubtitle", {
          defaultValue:
            "Capture contributions and optional file links that support your appraisal, goals, or PDP.",
        })}
        rightContent={
          <AppraisalGuideButton
            onClick={() => setHelpKey("evidence")}
            label={t("app.appraisal.evidence.guideBtn", {
              defaultValue: "How evidence works",
            })}
          />
        }
      />

      {loading ? <div className="appraisal-muted">Loading…</div> : null}
      {error ? <div className="appraisal-error">{error}</div> : null}
      {message ? <div className="appraisal-success">{message}</div> : null}

      {!loading && (
        <section className="appraisal-settings-panel appraisal-evidence-panel">
          <div className="appraisal-settings-panel__head appraisal-evidence-panel__head">
            <div>
              <AppraisalSectionHeader
                icon="bi-folder-symlink"
                title={t("app.appraisal.evidence.addTitle", {
                  defaultValue: "Add evidence",
                })}
                helpKey="evidence"
                onOpenHelp={setHelpKey}
                tone="evidence"
              />
              <p className="appraisal-framework-hint">
                {t("app.appraisal.evidence.hint", {
                  defaultValue:
                    "Proof of contribution — notes and optional file links that back up your appraisal, goals, or PDP.",
                })}
              </p>
            </div>
            <div className="appraisal-goals-panel__stats">
              <div className="appraisal-goals-stat">
                <strong>{rows.length}</strong>
                <span>
                  {t("app.appraisal.evidence.totalLabel", { defaultValue: "Total" })}
                </span>
              </div>
              <div className="appraisal-goals-stat">
                <strong>{completedCount}</strong>
                <span>
                  {t("app.appraisal.evidence.doneLabel", {
                    defaultValue: "Completed",
                  })}
                </span>
              </div>
            </div>
          </div>

          <form onSubmit={onSave} className="appraisal-framework-form">
            <label className="appraisal-field">
              {t("app.appraisal.evidence.headline", { defaultValue: "Headline" })}
              <input
                required
                disabled={saving}
                maxLength={APPRAISAL_LIMITS.headline}
                placeholder={t("app.appraisal.evidence.headlinePlaceholder", {
                  defaultValue: "e.g. Led migration of billing API to v2",
                })}
                value={form.headline}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    headline: clampLen(e.target.value, APPRAISAL_LIMITS.headline),
                  }))
                }
              />
              <AppraisalCharCount value={form.headline} max={APPRAISAL_LIMITS.headline} />
            </label>

            <label className="appraisal-field">
              {t("app.appraisal.evidence.contribution", {
                defaultValue: "Personal contribution",
              })}
              <textarea
                rows={3}
                disabled={saving}
                maxLength={APPRAISAL_LIMITS.longText}
                placeholder={t("app.appraisal.evidence.contributionPlaceholder", {
                  defaultValue: "What you did, the impact, and any metrics…",
                })}
                value={form.personalContribution}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    personalContribution: clampLen(e.target.value, APPRAISAL_LIMITS.longText),
                  }))
                }
              />
              <AppraisalCharCount
                value={form.personalContribution}
                max={APPRAISAL_LIMITS.longText}
              />
            </label>

            <div className="appraisal-lms-form-grid">
              <label className="appraisal-field">
                {t("app.appraisal.evidence.fileName", { defaultValue: "File name" })}
                <input
                  disabled={saving}
                  maxLength={APPRAISAL_LIMITS.fileName}
                  placeholder={t("app.appraisal.evidence.fileNamePlaceholder", {
                    defaultValue: "e.g. Q3 impact summary.pdf",
                  })}
                  value={form.fileName}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      fileName: clampLen(e.target.value, APPRAISAL_LIMITS.fileName),
                    }))
                  }
                />
                <AppraisalCharCount value={form.fileName} max={APPRAISAL_LIMITS.fileName} />
              </label>
              <label className="appraisal-field">
                {t("app.appraisal.evidence.fileUrl", { defaultValue: "File URL" })}
                <input
                  type="url"
                  disabled={saving}
                  placeholder="https://"
                  value={form.fileUrl}
                  onChange={(e) => setForm((p) => ({ ...p, fileUrl: e.target.value }))}
                />
              </label>
            </div>

            <div className="appraisal-lms-checks appraisal-evidence-checks">
              <label
                className={`appraisal-check-option${form.isCompleted ? " is-on" : ""}`}
              >
                <input
                  type="checkbox"
                  disabled={saving}
                  checked={!!form.isCompleted}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, isCompleted: e.target.checked }))
                  }
                />
                <span className="appraisal-check-option__box" aria-hidden>
                  <i className="bi bi-check-lg" />
                </span>
                <span className="appraisal-check-option__text">
                  <strong>
                    {t("app.appraisal.evidence.markCompleted", {
                      defaultValue: "Mark as completed",
                    })}
                  </strong>
                  <small>
                    {t("app.appraisal.evidence.markCompletedHint", {
                      defaultValue:
                        "Show this item as finished contribution ready for review",
                    })}
                  </small>
                </span>
              </label>
            </div>

            <div className="appraisal-framework-form__actions">
              <button type="submit" className="appraisal-btn primary" disabled={saving}>
                {saving
                  ? t("app.common.saving", { defaultValue: "Saving…" })
                  : t("app.appraisal.evidence.save", {
                      defaultValue: "Save evidence",
                    })}
              </button>
            </div>
          </form>

          <div className="appraisal-framework-list-block">
            <div className="appraisal-framework-list-block__head">
              <span>
                {t("app.appraisal.evidence.listTitle", {
                  defaultValue: "Your evidence",
                })}
              </span>
              <span className="appraisal-framework-count">{rows.length}</span>
            </div>

            {rows.length === 0 ? (
              <div className="appraisal-framework-empty">
                <i className="bi bi-folder-symlink" aria-hidden />
                <p>
                  {t("app.appraisal.evidence.empty", {
                    defaultValue:
                      "No evidence yet. Add your first contribution above.",
                  })}
                </p>
              </div>
            ) : (
              <ul className="appraisal-framework-items">
                {rows.map((r) => (
                  <li
                    key={r.id}
                    className={`appraisal-framework-item appraisal-evidence-item${
                      r.isCompleted ? " is-completed" : ""
                    }`}
                  >
                    <div className="appraisal-framework-item__body">
                      <div className="appraisal-framework-item__meta">
                        <span
                          className={`appraisal-lms-status${
                            r.isCompleted ? " is-active" : " is-inactive"
                          }`}
                        >
                          {r.isCompleted
                            ? t("app.appraisal.evidence.completed", {
                                defaultValue: "Completed",
                              })
                            : t("app.appraisal.evidence.inProgress", {
                                defaultValue: "In progress",
                              })}
                        </span>
                        {r.createdDate ? (
                          <span className="appraisal-lms-pill appraisal-lms-pill--soft">
                            {new Date(r.createdDate).toLocaleDateString()}
                          </span>
                        ) : null}
                      </div>
                      <strong>{r.headline || "—"}</strong>
                      {r.personalContribution ? (
                        <span>{r.personalContribution}</span>
                      ) : null}
                      {r.fileUrl ? (
                        <span className="appraisal-lms-item__link">
                          <i className="bi bi-link-45deg" aria-hidden />
                          <a href={r.fileUrl} target="_blank" rel="noreferrer">
                            {r.fileName || r.fileUrl}
                          </a>
                        </span>
                      ) : r.fileName ? (
                        <span className="appraisal-lms-item__link">
                          <i className="bi bi-file-earmark" aria-hidden />
                          {r.fileName}
                        </span>
                      ) : null}
                    </div>
                    <div className="appraisal-lms-item__actions">
                      <button
                        type="button"
                        className="appraisal-list-delete"
                        onClick={() => openDeleteConfirm(r)}
                        title={t("app.common.delete", { defaultValue: "Delete" })}
                      >
                        <i className="bi bi-trash3" aria-hidden />
                        <span>
                          {t("app.common.remove", { defaultValue: "Remove" })}
                        </span>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {deleteConfirm ? (
        <div
          className="appraisal-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="appraisal-evidence-delete-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !deleteLoading) setDeleteConfirm(null);
          }}
        >
          <div className="appraisal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="appraisal-modal__header">
              <div>
                <p className="appraisal-modal__eyebrow">
                  {t("app.appraisal.evidenceTitle", { defaultValue: "Evidence" })}
                </p>
                <h2
                  className="appraisal-modal__title"
                  id="appraisal-evidence-delete-title"
                >
                  {deleteConfirm.title}
                </h2>
              </div>
              <button
                type="button"
                className="appraisal-btn"
                disabled={deleteLoading}
                onClick={() => setDeleteConfirm(null)}
              >
                {t("app.common.close", { defaultValue: "Close" })}
              </button>
            </div>
            <div className="appraisal-modal__body">
              <div className="appraisal-modal__summary">
                <div className="appraisal-modal__icon" aria-hidden>
                  <i className="bi bi-trash3" />
                </div>
                <p className="appraisal-modal__lead">{deleteConfirm.body}</p>
              </div>
              {deleteConfirm.name ? (
                <div className="appraisal-modal__target">
                  <p className="appraisal-modal__target-label">
                    {t("app.appraisal.evidence.deleteTarget", {
                      defaultValue: "Evidence",
                    })}
                  </p>
                  <p className="appraisal-modal__target-name">{deleteConfirm.name}</p>
                </div>
              ) : null}
              <div className="appraisal-modal__actions">
                <button
                  type="button"
                  className="appraisal-btn"
                  disabled={deleteLoading}
                  onClick={() => setDeleteConfirm(null)}
                >
                  {t("app.common.cancel", { defaultValue: "Cancel" })}
                </button>
                <button
                  type="button"
                  className="appraisal-btn danger"
                  disabled={deleteLoading}
                  onClick={confirmDelete}
                >
                  {deleteLoading
                    ? t("app.common.deleting", { defaultValue: "Deleting…" })
                    : t("app.common.remove", { defaultValue: "Remove" })}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <AppraisalHelpModal helpKey={helpKey} onClose={() => setHelpKey(null)} />
    </div>
  );
};

export default AppraisalEvidencePage;
